import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, where, orderBy, getDocs, doc, updateDoc, deleteDoc, Timestamp } from 'firebase/firestore';
import { db } from './firebase';
import { useAdmin } from './hooks/useAdmin';
import MarketplaceItemDetail from './MarketplaceItemDetail';
import './Marketplace.css';

// Item categories
const CATEGORIES = [
  { id: 'dresses', label: 'Dresses', icon: '👗' },
  { id: 'shoes', label: 'Shoes', icon: '👠' },
  { id: 'costumes', label: 'Costumes', icon: '🎭' },
  { id: 'accessories', label: 'Accessories', icon: '💎' },
  { id: 'practice-wear', label: 'Practice Wear', icon: '🩱' },
  { id: 'jewelry', label: 'Jewelry', icon: '📿' },
  { id: 'other', label: 'Other', icon: '📦' }
];

const STATUS_TABS = [
  { id: 'pending', label: 'Pending Review', icon: '⏳' },
  { id: 'approved', label: 'Approved', icon: '✅' },
  { id: 'rejected', label: 'Rejected', icon: '❌' },
  { id: 'sold', label: 'Sold', icon: '💰' }
];

export default function AdminMarketplaceReview() {
  const { isAdmin, loading: authLoading, user } = useAdmin();
  
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [stats, setStats] = useState({
    pending: 0,
    approved: 0,
    rejected: 0,
    sold: 0
  });
  const [selectedItem, setSelectedItem] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectDialog, setShowRejectDialog] = useState(null);

  useEffect(() => {
    if (isAdmin) {
      fetchItems();
      fetchStats();
    }
  }, [isAdmin, activeTab]);

  const fetchStats = async () => {
    try {
      const itemsRef = collection(db, 'marketplaceItems');
      
      // Fetch counts for each status
      const pendingSnapshot = await getDocs(query(itemsRef, where('status', '==', 'pending')));
      const approvedSnapshot = await getDocs(query(itemsRef, where('status', '==', 'approved')));
      const rejectedSnapshot = await getDocs(query(itemsRef, where('status', '==', 'rejected')));
      const soldSnapshot = await getDocs(query(itemsRef, where('status', '==', 'sold')));
      
      setStats({
        pending: pendingSnapshot.size,
        approved: approvedSnapshot.size,
        rejected: rejectedSnapshot.size,
        sold: soldSnapshot.size
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchItems = async () => {
    setLoading(true);
    try {
      const itemsRef = collection(db, 'marketplaceItems');
      const q = query(
        itemsRef,
        where('status', '==', activeTab),
        orderBy('createdAt', 'desc')
      );
      
      const snapshot = await getDocs(q);
      const fetchedItems = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setItems(fetchedItems);
    } catch (error) {
      console.error('Error fetching items:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (itemId) => {
    setActionLoading(itemId);
    try {
      await updateDoc(doc(db, 'marketplaceItems', itemId), {
        status: 'approved',
        approvedAt: Timestamp.now(),
        approvedBy: user.uid,
        updatedAt: Timestamp.now()
      });
      
      // Update local state
      setItems(prev => prev.filter(item => item.id !== itemId));
      setStats(prev => ({
        ...prev,
        pending: prev.pending - 1,
        approved: prev.approved + 1
      }));
    } catch (error) {
      console.error('Error approving item:', error);
      alert('Failed to approve item. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (itemId) => {
    if (!rejectionReason.trim()) {
      alert('Please provide a rejection reason');
      return;
    }
    
    setActionLoading(itemId);
    try {
      await updateDoc(doc(db, 'marketplaceItems', itemId), {
        status: 'rejected',
        rejectedAt: Timestamp.now(),
        rejectedBy: user.uid,
        rejectionReason: rejectionReason.trim(),
        updatedAt: Timestamp.now()
      });
      
      // Update local state
      setItems(prev => prev.filter(item => item.id !== itemId));
      setStats(prev => ({
        ...prev,
        pending: prev.pending - 1,
        rejected: prev.rejected + 1
      }));
      
      setShowRejectDialog(null);
      setRejectionReason('');
    } catch (error) {
      console.error('Error rejecting item:', error);
      alert('Failed to reject item. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Are you sure you want to permanently delete this listing?')) {
      return;
    }
    
    setActionLoading(itemId);
    try {
      await deleteDoc(doc(db, 'marketplaceItems', itemId));
      
      // Update local state
      const deletedItem = items.find(item => item.id === itemId);
      setItems(prev => prev.filter(item => item.id !== itemId));
      setStats(prev => ({
        ...prev,
        [deletedItem?.status || activeTab]: prev[deletedItem?.status || activeTab] - 1
      }));
    } catch (error) {
      console.error('Error deleting item:', error);
      alert('Failed to delete item. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRestore = async (itemId) => {
    setActionLoading(itemId);
    try {
      await updateDoc(doc(db, 'marketplaceItems', itemId), {
        status: 'pending',
        rejectionReason: null,
        rejectedAt: null,
        rejectedBy: null,
        updatedAt: Timestamp.now()
      });
      
      // Update local state
      setItems(prev => prev.filter(item => item.id !== itemId));
      setStats(prev => ({
        ...prev,
        rejected: prev.rejected - 1,
        pending: prev.pending + 1
      }));
    } catch (error) {
      console.error('Error restoring item:', error);
      alert('Failed to restore item. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const formatPrice = (price) => {
    if (!price) return 'N/A';
    return `€${Number(price).toLocaleString()}`;
  };

  const getCategoryLabel = (categoryId) => {
    const cat = CATEGORIES.find(c => c.id === categoryId);
    return cat ? cat.label : categoryId;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="admin-marketplace-review">
        <div className="marketplace-loading">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // Check admin access
  if (!isAdmin) {
    return (
      <div className="admin-marketplace-review">
        <div className="admin-review-header">
          <h1>🔒 Access Denied</h1>
          <p>You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-marketplace-review">
      {/* Header */}
      <div className="admin-review-header">
        <h1>🛡️ Marketplace Admin</h1>
        <p>Review and manage marketplace listings</p>
        
        <div className="review-stats">
          <div className="review-stat">
            <span className="stat-number">{stats.pending}</span>
            <span className="stat-label">Pending</span>
          </div>
          <div className="review-stat">
            <span className="stat-number">{stats.approved}</span>
            <span className="stat-label">Approved</span>
          </div>
          <div className="review-stat">
            <span className="stat-number">{stats.sold}</span>
            <span className="stat-label">Sold</span>
          </div>
        </div>
      </div>

      {/* Back to Marketplace */}
      <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
        <Link to="/marketplace" className="marketplace-btn marketplace-btn-secondary">
          <span>←</span>
          <span>Back to Marketplace</span>
        </Link>
      </div>

      {/* Status Tabs */}
      <div className="review-tabs">
        {STATUS_TABS.map(tab => (
          <button
            key={tab.id}
            className={`review-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span>{tab.icon}</span>
            <span>{tab.label}</span>
            <span className="count">{stats[tab.id]}</span>
          </button>
        ))}
      </div>

      {/* Items List */}
      {loading ? (
        <div className="marketplace-loading">
          <div className="loading-spinner"></div>
          <p>Loading listings...</p>
        </div>
      ) : items.length === 0 ? (
        <div className="marketplace-empty">
          <div className="empty-icon">
            {STATUS_TABS.find(t => t.id === activeTab)?.icon || '📋'}
          </div>
          <h3>No {activeTab} listings</h3>
          <p>
            {activeTab === 'pending' 
              ? 'All listings have been reviewed!'
              : `No listings with status "${activeTab}" found.`}
          </p>
        </div>
      ) : (
        <div>
          {items.map(item => (
            <div key={item.id} className="review-item-card">
              <div className="review-item-header">
                {item.images && item.images[0] ? (
                  <img 
                    src={item.images[0]} 
                    alt={item.title}
                    className="review-item-image"
                  />
                ) : (
                  <div 
                    className="review-item-image" 
                    style={{ 
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '3rem'
                    }}
                  >
                    {CATEGORIES.find(c => c.id === item.category)?.icon || '📦'}
                  </div>
                )}
                
                <div className="review-item-info">
                  <h3>{item.title}</h3>
                  
                  <div className="review-item-meta">
                    <span className="review-meta-tag">
                      {getCategoryLabel(item.category)}
                    </span>
                    <span className="review-meta-tag">
                      {item.listingType === 'both' ? '💰 Sale & 📅 Rent' : 
                       item.listingType === 'rent' ? '📅 For Rent' : '💰 For Sale'}
                    </span>
                    {item.listingType !== 'rent' && (
                      <span className="review-meta-tag" style={{ color: '#ffd93d' }}>
                        {formatPrice(item.salePrice)}
                      </span>
                    )}
                    {item.listingType !== 'sale' && (
                      <span className="review-meta-tag">
                        {formatPrice(item.rentPricePerDay)}/day
                      </span>
                    )}
                  </div>
                  
                  <p className="review-item-description">
                    {item.description?.length > 200 
                      ? item.description.substring(0, 200) + '...'
                      : item.description}
                  </p>
                </div>
              </div>

              <div className="review-seller-info">
                <h4>Seller Information</h4>
                <div className="seller-details">
                  <span className="seller-name">{item.sellerName}</span>
                  {item.isVerifiedSeller && (
                    <span className="verified-badge">
                      <span>✓</span>
                      <span>Verified Dancer</span>
                    </span>
                  )}
                  <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}>
                    {item.sellerEmail}
                  </span>
                </div>
                <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: 'rgba(255,255,255,0.5)' }}>
                  Submitted: {formatDate(item.createdAt)}
                </div>
                
                {item.rejectionReason && (
                  <div style={{ 
                    marginTop: '0.75rem', 
                    padding: '0.75rem',
                    background: 'rgba(233, 69, 96, 0.1)',
                    borderRadius: '8px',
                    borderLeft: '3px solid #e94560'
                  }}>
                    <strong style={{ color: '#ff8a80', fontSize: '0.85rem' }}>Rejection Reason:</strong>
                    <p style={{ margin: '0.25rem 0 0', color: 'rgba(255,255,255,0.8)' }}>
                      {item.rejectionReason}
                    </p>
                  </div>
                )}
              </div>

              {/* Rejection Dialog */}
              {showRejectDialog === item.id && (
                <div style={{
                  padding: '1rem',
                  background: 'rgba(233, 69, 96, 0.1)',
                  borderRadius: '10px',
                  marginBottom: '1rem'
                }}>
                  <label style={{ color: 'white', display: 'block', marginBottom: '0.5rem' }}>
                    Rejection Reason:
                  </label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Explain why this listing is being rejected..."
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border: '1px solid rgba(255,255,255,0.2)',
                      background: 'rgba(255,255,255,0.05)',
                      color: 'white',
                      minHeight: '80px',
                      marginBottom: '0.75rem'
                    }}
                  />
                  <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                    <button
                      className="review-btn view"
                      onClick={() => {
                        setShowRejectDialog(null);
                        setRejectionReason('');
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      className="review-btn reject"
                      onClick={() => handleReject(item.id)}
                      disabled={actionLoading === item.id}
                    >
                      {actionLoading === item.id ? 'Rejecting...' : 'Confirm Rejection'}
                    </button>
                  </div>
                </div>
              )}

              <div className="review-actions">
                <button
                  className="review-btn view"
                  onClick={() => setSelectedItem(item)}
                >
                  <span>👁️</span>
                  <span>View Details</span>
                </button>
                
                {activeTab === 'pending' && (
                  <>
                    <button
                      className="review-btn approve"
                      onClick={() => handleApprove(item.id)}
                      disabled={actionLoading === item.id}
                    >
                      <span>✅</span>
                      <span>{actionLoading === item.id ? 'Approving...' : 'Approve'}</span>
                    </button>
                    
                    <button
                      className="review-btn reject"
                      onClick={() => setShowRejectDialog(item.id)}
                      disabled={actionLoading === item.id}
                    >
                      <span>❌</span>
                      <span>Reject</span>
                    </button>
                  </>
                )}
                
                {activeTab === 'rejected' && (
                  <button
                    className="review-btn approve"
                    onClick={() => handleRestore(item.id)}
                    disabled={actionLoading === item.id}
                  >
                    <span>↩️</span>
                    <span>{actionLoading === item.id ? 'Restoring...' : 'Restore to Pending'}</span>
                  </button>
                )}
                
                <button
                  className="review-btn reject"
                  onClick={() => handleDelete(item.id)}
                  disabled={actionLoading === item.id}
                  style={{ background: 'rgba(233, 69, 96, 0.3)' }}
                >
                  <span>🗑️</span>
                  <span>Delete</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Item Detail Modal */}
      {selectedItem && (
        <div className="marketplace-modal-overlay" onClick={() => setSelectedItem(null)}>
          <div className="marketplace-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '1000px' }}>
            <div className="modal-header">
              <h2>{selectedItem.title}</h2>
              <button className="modal-close" onClick={() => setSelectedItem(null)}>×</button>
            </div>
            <div className="modal-content">
              <MarketplaceItemDetail 
                item={selectedItem}
                onClose={() => setSelectedItem(null)}
                isOwner={false}
                isAdmin={true}
                onUpdate={() => {
                  fetchItems();
                  fetchStats();
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
